import { Button, CircularProgress, MenuItem, TextField } from '@mui/material';
import { Bot, Link2 } from 'lucide-react';
import React from 'react';
import type { Agent } from '../../types';
import { useTranslation } from 'react-i18next';

export interface BindingFormState {
  agentPublicId: string;
  externalTenantId: string;
  externalTenantType: string;
}

export interface ProvisionFormState {
  externalTenantId: string;
  externalTenantType: string;
  templateAgentPublicId: string;
  name: string;
}

interface ClientFormProps<T> {
  agents: Agent[];
  form: T;
  setForm: (form: T) => void;
  saving: boolean;
  clientSelected: boolean;
  onSubmit: () => void;
}

export const BindExistingAgentForm: React.FC<ClientFormProps<BindingFormState>> = ({
  agents,
  form,
  setForm,
  saving,
  clientSelected,
  onSubmit,
}) => {
  const { t } = useTranslation();

  return (
    <div className="rounded-lg border border-gray-200 p-3 dark:border-slate-700">
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-slate-200">
        <Link2 size={16} />
        {t('apiKeys.integrationClients.bindExistingAgent')}
      </div>
      <div className="grid gap-2">
        <TextField
          select
          fullWidth
          size="small"
          label={t('apiKeys.modal.agentResource')}
          value={form.agentPublicId}
          onChange={(e) => setForm({ ...form, agentPublicId: e.target.value })}
        >
          <MenuItem value="">{t('apiKeys.modal.none')}</MenuItem>
          {agents.map((agent) => (
            <MenuItem key={agent.publicId} value={agent.publicId}>
              {agent.name} · {agent.publicId}
            </MenuItem>
          ))}
        </TextField>
        <div className="grid gap-2 md:grid-cols-2">
          <TextField
            fullWidth
            size="small"
            label={t('apiKeys.integrationClients.externalTenantId')}
            placeholder="salon_456"
            value={form.externalTenantId}
            onChange={(e) => setForm({ ...form, externalTenantId: e.target.value })}
          />
          <TextField
            fullWidth
            size="small"
            label={t('apiKeys.integrationClients.externalTenantType')}
            value={form.externalTenantType}
            onChange={(e) => setForm({ ...form, externalTenantType: e.target.value })}
          />
        </div>
        <Button
          type="button"
          variant="outlined"
          startIcon={saving ? <CircularProgress size={16} /> : <Link2 size={16} />}
          disabled={
            !clientSelected || !form.agentPublicId || !form.externalTenantId.trim() || saving
          }
          onClick={onSubmit}
        >
          {t('apiKeys.integrationClients.saveBinding')}
        </Button>
      </div>
    </div>
  );
};

export const ProvisionAgentForm: React.FC<ClientFormProps<ProvisionFormState>> = ({
  agents,
  form,
  setForm,
  saving,
  clientSelected,
  onSubmit,
}) => {
  const { t } = useTranslation();

  return (
    <div className="rounded-lg border border-gray-200 p-3 dark:border-slate-700">
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-slate-200">
        <Bot size={16} />
        {t('apiKeys.integrationClients.provisionTenantAgent')}
      </div>
      <div className="grid gap-2">
        <div className="grid gap-2 md:grid-cols-2">
          <TextField
            fullWidth
            size="small"
            label={t('apiKeys.integrationClients.externalTenantId')}
            placeholder="salon_456"
            value={form.externalTenantId}
            onChange={(e) => setForm({ ...form, externalTenantId: e.target.value })}
          />
          <TextField
            fullWidth
            size="small"
            label={t('apiKeys.integrationClients.externalTenantType')}
            value={form.externalTenantType}
            onChange={(e) => setForm({ ...form, externalTenantType: e.target.value })}
          />
        </div>
        <TextField
          fullWidth
          size="small"
          label={t('apiKeys.integrationClients.agentName')}
          placeholder="NailMap Salon 456 Assistant"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        <TextField
          select
          fullWidth
          size="small"
          label={t('apiKeys.integrationClients.templateAgent')}
          value={form.templateAgentPublicId}
          onChange={(e) =>
            setForm({
              ...form,
              templateAgentPublicId: e.target.value,
            })
          }
        >
          <MenuItem value="">{t('apiKeys.modal.none')}</MenuItem>
          {agents.map((agent) => (
            <MenuItem key={agent.publicId} value={agent.publicId}>
              {agent.name} · {agent.publicId}
            </MenuItem>
          ))}
        </TextField>
        <Button
          type="button"
          variant="outlined"
          startIcon={saving ? <CircularProgress size={16} /> : <Bot size={16} />}
          disabled={!clientSelected || !form.externalTenantId.trim() || saving}
          onClick={onSubmit}
        >
          {t('apiKeys.integrationClients.provisionAgent')}
        </Button>
      </div>
    </div>
  );
};
